import React, { Fragment, useEffect, useState } from 'react';

import { formatAndRoundMoney, formatFigure } from 'Helpers/formatService';

import Tooltip from 'CommonComponents/Tooltip';

import { useToast } from 'Hooks/useToast';

import MinusIcon from 'Assets/icons/minus.svg';
import PlusIcon from 'Assets/icons/plus.svg';

export default function ExpensePacksDashboard({ fetchDashboardUrl }) {
    const [dashboard, setDashboard] = useState({});
    const [venues, setVenues] = useState({});
    const [displayVenues, setDisplayVenues] = useState({});
    const [collapsed, setCollapsed] = useState(true);
    const [simplePacks, setSimplePacks] = useState([]);
    const [contractorPacks, setContractorPacks] = useState([]);

    const toast = useToast();

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = () => {
        axios
            .get(fetchDashboardUrl)
            .then(({ data }) => {
                setDashboard(data.summary);
                setVenues(data.summary.venues);
                setSimplePacks(data.summary.simple_packs);
                setContractorPacks(data.summary.contractor_packs);

                const displayed = JSON.parse(JSON.stringify(displayVenues));
                Object.keys(data.summary.venues).forEach((id) => {
                    displayed[id] = id in displayed ? displayed.id : true;
                });
                setDisplayVenues(displayed);
            })
            .catch(() => {
                toast.error($t('An error occurred, please try again'));
            });
    };

    const toggleCollapsed = () => {
        setCollapsed(!collapsed);
    };

    const toggleVenue = (id) => {
        const displayed = JSON.parse(JSON.stringify(displayVenues));
        displayed[id] = !displayed[id];
        setDisplayVenues(displayed);
    };

    return (
        <>
            <div className="tw-mb-2">
                <div className="tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary">
                    <div className="tw-flex tw-justify-between tw-items-start">
                        <h5 className="table__header">{$t('Venues')}</h5>
                        <div className="tw-flex tw-gap-x-1.5">
                            <button className="table__icon-btn tw-text-white" onClick={toggleCollapsed}>
                                {!!collapsed && <PlusIcon className="tw-w-3.5 tw-h-3.5" />}
                                {!collapsed && <MinusIcon className="tw-w-3.5 tw-h-3.5" />}
                            </button>
                        </div>
                    </div>
                    {!collapsed && (
                        <div className="tw-grid tw-grid-cols-5 tw-gap-2">
                            {Object.values(venues).map((venue) => {
                                const id = venue.id;
                                const venueDisplayed = displayVenues[id] || false;

                                return (
                                    <div key={venue.id} className="tw-px-1">
                                        <div className="toggl-input" onClick={() => toggleVenue(id)}>
                                            <input
                                                id={`show-button-${id}`}
                                                className="toggl-input-checkbox"
                                                name="active"
                                                type="checkbox"
                                                checked={!!venueDisplayed}
                                                onChange={() => toggleVenue(id)}
                                                data-action="toggle-slider"
                                            />
                                            <label className="toggl-input-label tw-mb-0"></label>
                                        </div>
                                        <span>{venue.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            <DashboardTable venues={venues} simplePacks={simplePacks} contractorPacks={contractorPacks} displayVenues={displayVenues} />
        </>
    );
}

const DashboardTable = (props) => {
    const countCols = () => {
        return Object.keys(props.venues).length * 5 + 1;
    };

    return (
        <div className="inbud-table_wrapper tw-overflow-auto tw-max-h-screen tw-relative">
            <table className="inbud-table inbud-table_first-sticky">
                <thead className="tw-sticky tw-top-0" style={{ zIndex: 15 }}>
                    <tr className="tw-cursor-pointer tw-font-bold">
                        <th className="tw-sticky tw-min-w-48  group-end-th"></th>
                        {Object.keys(props.venues).map((venueId, index) => {
                            const venueData = props.venues[venueId];

                            if (props.displayVenues[venueId]) {
                                return (
                                    <th
                                        className={`tw-p-4 tw-sticky ${index + 1 === Object.keys(props.venues).length ? '' : 'group-end-th'}`}
                                        key={venueId}
                                        colSpan={5}
                                    >
                                        {venueData.name}
                                    </th>
                                );
                            }
                            return null;
                        })}
                    </tr>
                </thead>
                <tbody>
                    {props.simplePacks.length > 0 && (
                        <>
                            <PackTypeHeader venues={props.venues} contractor={false} displayVenues={props.displayVenues} />
                            {props.simplePacks.map((pack) => {
                                return <PackRow key={`pack-${pack.id}`} pack={pack} venues={props.venues} displayVenues={props.displayVenues} />;
                            })}
                        </>
                    )}
                    {props.simplePacks.length > 0 && props.contractorPacks.length > 0 && (
                        <tr>
                            <td colSpan={countCols()}></td>
                        </tr>
                    )}
                    {props.contractorPacks.length > 0 && (
                        <>
                            <PackTypeHeader venues={props.venues} contractor={true} displayVenues={props.displayVenues} />
                            {props.contractorPacks.map((pack) => {
                                return <PackRow key={`pack-${pack.id}`} pack={pack} venues={props.venues} displayVenues={props.displayVenues} />;
                            })}
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );
};

const PackTypeHeader = (props) => {
    return (
        <tr className="tw-font-bold">
            <td className="tw-min-w-16 group-end-td">{props.contractor ? $t('Contractor Expense Packs') : $t('Simple Expense Packs')}</td>
            {Object.keys(props.venues).map((venueId, index) => {
                if (props.displayVenues[venueId]) {
                    return (
                        <Fragment key={`header-${venueId}`}>
                            <td className="tw-min-w-16 tw-cursor-help ">
                                <Tooltip
                                    placement="top"
                                    tooltipContent={$t('Number of days that the cost pack is allocated to for this venue')}
                                    tooltipType="text"
                                >
                                    <span>{$t('Allocated days')}</span>
                                </Tooltip>
                            </td>
                            <td className="tw-min-w-16 tw-cursor-help ">
                                <Tooltip
                                    placement="top"
                                    tooltipContent={$t('Number of days that have one or more sessions occurring for this venue')}
                                    tooltipType="text"
                                >
                                    <span>{$t('Session days')}</span>
                                </Tooltip>
                            </td>
                            <td className="tw-min-w-16 tw-cursor-help ">
                                <Tooltip placement="top" tooltipContent={$t('Number of session days that have cost pack allocated')} tooltipType="text">
                                    <span>{$t('Session days with pack')}</span>
                                </Tooltip>
                            </td>
                            <td className="tw-min-w-16 tw-cursor-help ">
                                <Tooltip
                                    placement="top"
                                    tooltipContent={$t('Number of days that have no sessions but have cost packs allocated')}
                                    tooltipType="text"
                                >
                                    <span>{$t('Non Session days')}</span>
                                </Tooltip>
                            </td>
                            <td className={`tw-min-w-16 ${index + 1 === Object.keys(props.venues).length ? '' : 'group-end-td'}`}>{$t('Total Amount')}</td>
                        </Fragment>
                    );
                }

                return null;
            })}
        </tr>
    );
};

const PackRow = ({ pack, venues, displayVenues }) => {
    return (
        <tr key={`pack-${pack.id}`}>
            <td className="group-end-td">{pack.name}</td>
            {Object.keys(venues).map((venueId, index) => {
                const venueData = pack.venues[venueId] || null;
                const generalVenueData = venues[venueId];
                if (displayVenues[venueId]) {
                    return (
                        <Fragment key={`pack-${pack.id}-venue-${venueId}`}>
                            <td className="">{formatFigure(venueData?.days_pack_allocated_to, String)}</td>
                            <td className="">{formatFigure(generalVenueData.total_days_with_sessions, String)}</td>
                            <td className="">{formatFigure(venueData?.session_days_with_pack_allocated, String)}</td>
                            <td className="">{formatFigure(venueData?.non_session_days_with_pack_allocated, String)}</td>
                            <td className={`${index + 1 === Object.keys(venues).length ? '' : 'group-end-td'}`}>
                                {formatFigure(venueData?.total_amount, formatAndRoundMoney)}
                            </td>
                        </Fragment>
                    );
                }
                return null;
            })}
        </tr>
    );
};
